import { jsPDF } from 'jspdf'
import { company, CONTACT_EMAIL } from '../data/site'
import { formatDate } from './format'
import { invoiceLineSubtotal, invoiceTotals } from './invoice'
import type { AdminSettings } from '../types/admin'
import type { Invoice } from '../types/invoice'

/** Filnavn: LYKKEVAERK-Faktura-FAK-2026-0007.pdf */
export function invoiceFileName(invoiceNumber: string): string {
  return `LYKKEVAERK-Faktura-${invoiceNumber || 'kladde'}.pdf`
}

const INK: [number, number, number] = [22, 35, 47]
const MUTED: [number, number, number] = [107, 122, 136]
const LINE: [number, number, number] = [222, 218, 210]
const ACCENT: [number, number, number] = [200, 150, 62]

const MARGIN = 18
const PAGE_W = 210
const PAGE_H = 297
const RIGHT = PAGE_W - MARGIN

function kr(amount: number): string {
  const value = Number.isFinite(amount) ? amount : 0
  return `${value.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr.`
}

async function loadLogo(): Promise<string | null> {
  try {
    const response = await fetch(company.logoWide, { mode: 'cors' })
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

/**
 * Bygger fakturaen som printvenlig A4-PDF i LYKKEVÆRKs identitet.
 * Bruger altid de senest gemte fakturadata. Den interne note kommer
 * aldrig med på kundens PDF.
 */
export async function buildInvoicePdf(invoice: Invoice, settings: AdminSettings): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  doc.setFont('helvetica', 'normal')

  const logo = await loadLogo()
  let y = MARGIN

  if (logo) {
    try {
      const props = doc.getImageProperties(logo)
      const height = 13
      const width = (props.width / props.height) * height
      doc.addImage(logo, 'PNG', MARGIN, y, width, height)
    } catch {
      doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...INK)
      doc.text(settings.companyName || 'LYKKEVÆRK', MARGIN, y + 9)
    }
  } else {
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...INK)
    doc.text(settings.companyName || 'LYKKEVÆRK', MARGIN, y + 9)
  }

  const senderLines = [
    settings.companyName || 'LYKKEVÆRK',
    `CVR: ${settings.cvr}`,
    settings.phone,
    CONTACT_EMAIL,
  ].filter(Boolean)

  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...MUTED)
  senderLines.forEach((line, index) => {
    doc.text(line, RIGHT, y + 3 + index * 4.2, { align: 'right' })
  })

  y += 24

  /* Titel, nummer og datoer */
  doc.setFont('helvetica', 'bold').setFontSize(24).setTextColor(...INK)
  doc.text('Faktura', MARGIN, y)
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  doc.text(invoice.invoiceNumber, MARGIN, y + 6)

  doc.setFontSize(9)
  doc.text('Fakturadato', RIGHT - 26, y - 2, { align: 'right' })
  doc.text('Forfaldsdato', RIGHT - 26, y + 3, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(formatDate(invoice.invoiceDate), RIGHT, y - 2, { align: 'right' })
  doc.text(formatDate(invoice.dueDate), RIGHT, y + 3, { align: 'right' })

  y += 14
  doc.setDrawColor(...LINE).setLineWidth(0.3)
  doc.line(MARGIN, y, RIGHT, y)
  y += 7

  /* Modtager og afsender */
  doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...MUTED)
  doc.text('TIL', MARGIN, y)
  doc.text('FRA', RIGHT, y, { align: 'right' })

  const postal = [invoice.customerPostalCode, invoice.customerCity].filter(Boolean).join(' ')
  const toLines = [
    invoice.customerName || '—',
    invoice.customerAddress,
    postal,
    invoice.customerPhone,
    invoice.customerEmail,
  ].filter(Boolean)

  doc.setFontSize(10)
  toLines.forEach((line, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal')
    doc.setTextColor(...(index === 0 ? INK : MUTED))
    doc.text(line, MARGIN, y + 6 + index * 4.6)
  })
  senderLines.forEach((line, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal')
    doc.setTextColor(...(index === 0 ? INK : MUTED))
    doc.text(line, RIGHT, y + 6 + index * 4.6, { align: 'right' })
  })

  y += 6 + Math.max(toLines.length, senderLines.length) * 4.6 + 5
  doc.setDrawColor(...LINE)
  doc.line(MARGIN, y, RIGHT, y)
  y += 8

  /* Reference og betalingsbetingelser */
  const meta = [
    invoice.reference ? `Reference: ${invoice.reference}` : '',
    invoice.paymentTerms ? `Betaling: ${invoice.paymentTerms}` : '',
  ].filter(Boolean)
  if (meta.length > 0) {
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...MUTED)
    doc.text(meta.join('  ·  '), MARGIN, y)
    y += 8
  }

  /* Beskrivelse */
  if (invoice.description) {
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
    const wrapped = doc.splitTextToSize(invoice.description, RIGHT - MARGIN)
    doc.text(wrapped, MARGIN, y)
    y += wrapped.length * 4.6 + 6
  }

  /* Tabel med fakturalinjer */
  const colQty = MARGIN + 80
  const colUnit = MARGIN + 100
  const colPrice = MARGIN + 126
  const colDiscount = MARGIN + 144
  const colTotal = RIGHT

  function tableHeader() {
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    doc.text('BESKRIVELSE', MARGIN, y)
    doc.text('ANTAL', colQty, y, { align: 'right' })
    doc.text('ENHED', colUnit, y, { align: 'right' })
    doc.text('PRIS', colPrice, y, { align: 'right' })
    doc.text('RABAT', colDiscount, y, { align: 'right' })
    doc.text('I ALT', colTotal, y, { align: 'right' })
    y += 2.5
    doc.setDrawColor(...LINE)
    doc.line(MARGIN, y, RIGHT, y)
    y += 5
  }

  tableHeader()
  doc.setFont('helvetica', 'normal').setFontSize(10)

  invoice.lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line.description || '—', 74)
    const rowHeight = Math.max(wrapped.length * 4.4, 6)

    if (y + rowHeight > PAGE_H - 34) {
      doc.addPage()
      y = MARGIN
      tableHeader()
      doc.setFont('helvetica', 'normal').setFontSize(10)
    }

    doc.setTextColor(...INK)
    doc.text(wrapped, MARGIN, y)
    doc.setTextColor(...MUTED)
    doc.text(String(line.quantity ?? 0), colQty, y, { align: 'right' })
    doc.text(line.unit, colUnit, y, { align: 'right' })
    doc.text(kr(line.unitPrice), colPrice, y, { align: 'right' })
    doc.text(line.discount ? `${line.discount} %` : '—', colDiscount, y, { align: 'right' })
    doc.setTextColor(...INK)
    doc.text(kr(invoiceLineSubtotal(line)), colTotal, y, { align: 'right' })

    y += rowHeight + 2
    doc.setDrawColor(240, 238, 233)
    doc.line(MARGIN, y - 1, RIGHT, y - 1)
    y += 2
  })

  if (invoice.lines.length === 0) {
    doc.setTextColor(...MUTED).setFontSize(10)
    doc.text('Ingen linjer på fakturaen.', MARGIN, y)
    y += 8
  }

  /* Totaler */
  const totals = invoiceTotals(invoice.lines)
  if (y + 34 > PAGE_H - 30) {
    doc.addPage()
    y = MARGIN
  }
  y += 4

  const labelX = RIGHT - 52
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  doc.text('Subtotal ekskl. moms', labelX, y, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(kr(totals.subtotal), RIGHT, y, { align: 'right' })

  y += 5.5
  doc.setTextColor(...MUTED)
  doc.text('Moms', labelX, y, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(kr(totals.vatAmount), RIGHT, y, { align: 'right' })

  y += 4
  doc.setDrawColor(...LINE)
  doc.line(labelX - 24, y, RIGHT, y)
  y += 6.5

  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...INK)
  doc.text('Total inkl. moms', labelX, y, { align: 'right' })
  doc.text(kr(totals.total), RIGHT, y, { align: 'right' })

  y += 14

  /* Betalingsoplysninger og bemærkning til kunden */
  const blocks: { label: string; text: string }[] = [
    {
      label: 'BETALING',
      text: [
        invoice.paymentTerms,
        invoice.dueDate ? `Beløbet bedes indbetalt senest den ${formatDate(invoice.dueDate)}.` : '',
        `Anfør ${invoice.invoiceNumber} ved betaling.`,
      ]
        .filter(Boolean)
        .join(' '),
    },
    { label: 'BEMÆRKNING', text: invoice.customerNote },
  ].filter((block) => Boolean(block.text))

  blocks.forEach((block) => {
    const wrapped = doc.splitTextToSize(block.text, RIGHT - MARGIN - 10)
    const boxHeight = wrapped.length * 4.6 + 14

    if (y + boxHeight > PAGE_H - 26) {
      doc.addPage()
      y = MARGIN
    }

    doc.setFillColor(248, 246, 242)
    doc.rect(MARGIN, y, RIGHT - MARGIN, boxHeight, 'F')
    doc.setFillColor(...ACCENT)
    doc.rect(MARGIN, y, 1.2, boxHeight, 'F')

    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    doc.text(block.label, MARGIN + 6, y + 6)
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...INK)
    doc.text(wrapped, MARGIN + 6, y + 11.5)

    y += boxHeight + 6
  })

  /* Sidefod */
  const pages = doc.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    doc.setDrawColor(...LINE).setLineWidth(0.3)
    doc.line(MARGIN, PAGE_H - 18, RIGHT, PAGE_H - 18)
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...MUTED)
    doc.text(
      [settings.companyName || 'LYKKEVÆRK', `CVR: ${settings.cvr}`, settings.phone, CONTACT_EMAIL]
        .filter(Boolean)
        .join('  ·  '),
      MARGIN,
      PAGE_H - 12,
    )
    doc.text(`Side ${page} af ${pages}`, RIGHT, PAGE_H - 12, { align: 'right' })
  }

  return doc
}

export async function downloadInvoicePdf(
  invoice: Invoice,
  settings: AdminSettings,
): Promise<void> {
  const doc = await buildInvoicePdf(invoice, settings)
  doc.save(invoiceFileName(invoice.invoiceNumber))
}

/** Data-URL til forhåndsvisning i en indbygget PDF-visning. */
export async function invoicePdfDataUrl(
  invoice: Invoice,
  settings: AdminSettings,
): Promise<string> {
  const doc = await buildInvoicePdf(invoice, settings)
  return doc.output('datauristring')
}
